import React, { useState, useRef, useEffect } from "react";
import { ExternalLink, LayoutGrid } from "lucide-react";
import useAppSwitcher from "../Hooks/useAppSwitcher";
import { classNames } from "../utils";

const Tag = ({ tone, children }) => (
	<span
		className={classNames(
			"shrink-0 rounded-full px-2 py-0.5 text-[0.625rem] font-medium capitalize",
			tone === "amber" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-500",
		)}
	>
		{children}
	</span>
);

export const AppSwitcherList = ({ onSwitch }) => {
	const { apps, loading, failed, openApp, opening } = useAppSwitcher();

	return (
		<div>
			<p className="px-4 pb-1 text-[0.625rem] font-semibold uppercase tracking-wide text-gray-400">
				Obana apps
			</p>
			{apps.map((app) => {
				const held = Boolean(app.profile);
				const clickable = held && !app.current && !opening;
				return (
					<button
						key={app.key}
						type="button"
						disabled={!clickable}
						aria-current={app.current ? "page" : undefined}
						title={
							app.current
								? `Current platform (${app.label})`
								: !held && !loading
								? `You don't have a ${app.label} account`
								: `Open ${app.label} in a new tab`
						}
						onClick={() => {
							if (clickable) {
								openApp(app);
								onSwitch?.();
							}
						}}
						className={classNames(
							"flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-sm transition-colors",
							app.current && "bg-gray-50 font-medium",
							clickable && "text-gray-700 hover:bg-gray-50 cursor-pointer",
							!held && !app.current && "cursor-not-allowed opacity-40 bg-gray-50/30 text-gray-400",
							!clickable && !app.current && "cursor-not-allowed",
						)}
					>
						<span className="min-w-0">
							<span
								className={classNames(
									"block font-medium",
									!held && !app.current ? "text-gray-400" : "text-gray-800",
								)}
							>
								{app.label}
							</span>
							<span className="block truncate text-xs text-gray-500">{app.description}</span>
						</span>
						{app.current && <Tag>Current</Tag>}
						{!app.current && opening === app.key && (
							<span className="shrink-0 text-xs text-gray-400">Opening...</span>
						)}
						{!app.current && opening !== app.key && held && !app.profile.is_active && (
							<Tag tone="amber">{app.profile.status}</Tag>
						)}
						{!app.current && opening !== app.key && held && app.profile.is_active && (
							<ExternalLink className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
						)}
						{!app.current && !held && !loading && <Tag>No account</Tag>}
					</button>
				);
			})}
			{failed && <p className="px-4 py-1 text-xs text-gray-400">Could not check your other apps</p>}
		</div>
	);
};

const AppSwitcher = ({ onSwitch }) => (
	<div className="border-t border-gray-100 mt-2 pt-2">
		<AppSwitcherList onSwitch={onSwitch} />
	</div>
);

export const AppSwitcherMenu = () => {
	const [open, setOpen] = useState(false);
	const menuRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	return (
		<div className="relative" ref={menuRef}>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className="p-2 hover:bg-gray-100 rounded-[0.625rem] transition-colors relative flex items-center justify-center text-gray-600 hover:text-gray-900"
				title="Switch Obana app"
				aria-label="Switch Obana app"
			>
				<LayoutGrid className="w-5 h-5" />
			</button>
			{open && (
				<div className="absolute right-0 mt-2 w-72 rounded-xl bg-white shadow-xl border border-gray-100 py-2 z-50">
					<AppSwitcherList onSwitch={() => setOpen(false)} />
				</div>
			)}
		</div>
	);
};

export default AppSwitcher;
